/* eslint-disable jsx-a11y/anchor-is-valid */
import { lazy, Suspense, useEffect } from 'react'
import './App.scss'
import { API } from './core/API'
import { Smartico } from './core/Smartico'
import { useAppDispatch, useAppSelector } from './redux/hooks'
import { LoginStatus } from './redux/slices/userSlice'
import { setUserProfile } from './redux/slices/userPublicSlice'
import { RootState } from './redux/store'
import CasinoPage from './pages/CasinoPage/CasinoPage'
import ModalManager from './UI-components/Modals/ModalManager'
import Sidebar from './UI-components/Sidebar/Sidebar'
import Header from './UI-components/Header/Header'
import SportPage from './pages/SportPage/SportPage'
import ProfilePage from './pages/ProfilePage/ProfilePage'
import TournamentDetailsPage from './pages/TournamentDetailsPage/TournamentDetailsPage'
import TournamentListPage from './pages/TournamentListPage/TournamentListPage'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import GamePage from './pages/GamePage/GamePage'
import VipPage from './pages/VipPage/VipPage'
import BottomMenu from './UI-components/BottomMenu/BottomMenu'
import Utils from './core/Utils'
import BalanceModal from './UI-components/Modals/BalanceModal/BalanceModal'
import CashierModal from './UI-components/Modals/CashierModal/CashierModal'
import InboxModal from './UI-components/Modals/InboxModal/InboxModal'
import { toggleMenu } from './redux/slices/burgerMenuSlice'
import { EventName } from './core/enums/moneyOperations'
import StoreListPage from './pages/StoreListPage/StoreListPage'
import MinigamesListPage from './pages/MinigamesListPage/MinigamesListPage'
import MissionListPage from './pages/MissionListPage/MissionListPage'
import JackpotListPage from './pages/JackpotListPage/JackpotListPage'
import MatchxQuizPage from './pages/MatchxQuizPage/MatchxQuizPage'
import MiniGamePage from './pages/MiniGamePage/MiniGamePage'
import MiniGameDpRedirect from './UI-components/MiniGameDpRedirect/MiniGameDpRedirect'
import LeaderboardsPage from './pages/LeaderboardsPage/LeaderboardsPage'
import RafflePage from './pages/RafflePage/RafflePage'
import PrizeDropPage from './pages/PrizeDropPage/PrizeDropPage'
import MyCollectiblesPage from './pages/MyCollectiblesPage/MyCollectiblesPage'
import VibeStudioPage from './pages/VibeStudioPage/VibeStudioPage'
import useInactivity from './hooks/useInactivity'
import PrivateRoutes from './pages/Protected/ProtectedRoutes'
import { openModal } from './redux/slices/modalSlice'
import { ModalType } from './core/enums/Modals'
import PiggyBank from './UI-components/PiggyBank/PiggyBank'
import GamePageMobile from './pages/GamePage/GamePageMobile'

// The Smartico Experience (guided tour, phone, X-Ray) runs only on a local dev server with REACT_APP_XP_TOUR=on
// (.env.development.local); a production build never contains the src/xp folder
const XPLayer =
	process.env.NODE_ENV === 'development' && process.env.REACT_APP_XP_TOUR === 'on' ? lazy(() => import('./xp/XPLayer')) : null

const SERVER_EVENTS: string[] = [
	EventName.acc_deposit_approved,
	EventName.casino_bet_win,
	EventName.sport_bet_open,
	EventName.sport_bet_settled,
	EventName.sport_bet_selection_open,
	EventName.sport_bet_selection_settled,
	EventName.demo_fx_open_position,
	EventName.demo_fx_close_position,
]

// enum ActionType {
// 	None,
// 	CasinoBet,
// 	SportBet,
// 	OpenPosition,
// 	ClosePosition,
// 	SportBetSlipRequest,
// 	SportBetSlipResults,
// 	Deposit,
// 	General,
// 	GeneralTrading,
// 	BetBuddy,
// }

function App() {
	const isMobile = Utils.isMobile()
	const dispatch = useAppDispatch()
	const { loggedIn, user, user_id } = useAppSelector((state: RootState) => state.user)
	const { selectedLanguage } = useAppSelector((state: RootState) => state.language)
	const { active } = useAppSelector((state: RootState) => state.burger)
	console.log('user', user, loggedIn, user_id)
	useEffect(() => {
		API.checkCookieToken()
		API.waitForQR()
		!isMobile && dispatch(toggleMenu())
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (loggedIn === LoginStatus.LoggedIn && user) {
			Smartico.onReady(async (message) => {
				API.setUserId(message.user_id)
				API.setWallet()
				const refreshProfile = async () => {
					const smarticoApi = await Smartico.apiReady
					const profile = await smarticoApi.getUserProfile()
					if (profile) {
						dispatch(setUserProfile(profile))
					}
				}
				await refreshProfile()
				setTimeout(refreshProfile, 2000)
			})

			Smartico.identify(user)

			Smartico.lib().on('session_based_dp_detected', () => {})

			Smartico.lib().on('gf_starting', () => {
				setTimeout(() => {
					Smartico.action('gf-started')
				}, 2000)
			})

			Smartico.lib().on('gf_closing', () => {
				Smartico.action('gf-closed')
			})

			Smartico.lib().on('props_change', async () => {
				const smarticoApi = await Smartico.apiReady
				const profile = await smarticoApi.getUserProfile()
				if (profile) {
					dispatch(setUserProfile(profile))
				}
			})

			;(window as any)._smartico.changeLanguage(selectedLanguage)
		}
		if (loggedIn === LoginStatus.NotLoggedIn && Smartico.identified()) {
			Smartico.clear()
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedIn, user, user?.user_ext_id])

	const resetUser = async () => {
		if (loggedIn === LoginStatus.LoggedIn && Smartico.identified()) {
			Smartico.clear()
		}
		await API.duplicateUser()
		// let reloadTriggered = false

		window.location.reload()
	}

	const onTimeOut = () => {
		;(window as any)._smartico.event('client_action', { action: 'ResetUser' })
	}

	useInactivity(120000, onTimeOut)

	const onControlPopupSubmitted = (evenName?: string, eventData?: any, close: boolean = true) => {
		if (evenName) {
			if (SERVER_EVENTS.indexOf(evenName) > -1) {
				API.sendEventIce(evenName, { ...eventData, user_id }, user?.user_ext_id as string)
			} else {
				Smartico.lib().event(evenName, eventData)
			}
		}
	}

	;(window as any).__smarticoDPCustom = async (dp: any) => {
		switch (dp.action) {
			case 'resetuser': {
				resetUser()
				break
			}
			case 'openCashier': {
				dispatch(openModal({ modalType: ModalType.Cashier }))
				break
			}
			default: {
				return false
			}
		}
		return true
	}

	return (
		<div className={`App`}>
			<Router>
				<MiniGameDpRedirect />
				<Sidebar isClosed={!active} />
				<Header />
				<Routes>
					<Route element={<PrivateRoutes />}>
						<Route path="/tournament-list" element={<TournamentListPage />} />
						<Route path="/leaderboards" element={<LeaderboardsPage />} />
						<Route path="/raffle" element={<RafflePage />} />
						<Route path="/prize-drop" element={<PrizeDropPage />} />
						<Route path="/my-collectibles" element={<MyCollectiblesPage />} />
						<Route path="/vibe-studio" element={<VibeStudioPage />} />
						<Route path="/missions" element={<MissionListPage />} />
						<Route path="/matchx" element={<MinigamesListPage />} />
						<Route path="/mini-games" element={<MinigamesListPage />} />
						<Route path="/mini-game" element={<MiniGamePage />} />
						<Route path="/profile" element={<ProfilePage />} />
						<Route path="/quiz" element={<MinigamesListPage />} />
						<Route path="/store" element={<StoreListPage />} />
						<Route path="/matchx/:id" element={<MatchxQuizPage gameType="matchx" />} />
						<Route path="/jackpots" element={<JackpotListPage />} />
						<Route path="/quiz/:id" element={<MatchxQuizPage gameType="quiz" />} />
						<Route path="/vip" element={<VipPage />} />
						<Route path="/game/:ext_id" element={isMobile ? <GamePageMobile onSubmit={onControlPopupSubmitted} /> :<GamePage onSubmit={onControlPopupSubmitted} />} />
						<Route path="/tournament/:instance_id" element={<TournamentDetailsPage />} />
						<Route path="/wallet-page" element={<BalanceModal />} />
						<Route path="/cashier" element={<CashierModal onSubmit={onControlPopupSubmitted} />} />
						<Route path="/inbox" element={<InboxModal />} />
						<Route path="/sports" element={<SportPage onSubmit={onControlPopupSubmitted}  />} />
					</Route>
					<Route path="/" element={<CasinoPage />} />
				</Routes>
				{isMobile && <BottomMenu />}
				<ModalManager onSubmit={onControlPopupSubmitted} />
				{XPLayer && (
					<Suspense fallback={null}>
						<XPLayer />
					</Suspense>
				)}
			</Router>
			{loggedIn === LoginStatus.LoggedIn && <PiggyBank />}
		</div>
	)
}

export default App
